import React from 'react';

import {
    Column
} from 'devextreme-react/data-grid';

import { TColumn, TCellValueChange, TCellValueChangeCallback, TCellRenderTemplate, classNameDisabled } from 'types/columnDefs';
import { TRowData } from 'types/rowDefs';

import { CellFavourite } from './Cell.Favourite';
import { CellLink } from './Cell.Link';
import { CellDateTime } from './Cell.DateTime';

/* ======================================== */

type TEditorOptions = {
    onValueChanged?: (valueChange: TCellValueChange) => void;
};

export const setEditorOptions = (callback: TCellValueChangeCallback | undefined): TEditorOptions | undefined => {
    if (callback) {
        return {
            onValueChanged: (valueChange: TCellValueChange) =>
                callback(valueChange.value, valueChange.previousValue, valueChange.event, valueChange.element)
        };
    }
    return undefined;
};

/* ======================================== */

/**
 * Base implementation of adserve DataGrid Column
 * !! Do not export from the library !!
 * @private
 */
export const DataGridColumn = (
    col: TColumn,
    compact: boolean | undefined,
    onRowToggleFavourite: ((rowData: TRowData) => void) | undefined
) => {
    switch (col.type) {
        case 'favourite':
            const callRowToggle = (rowData: TRowData | undefined, template: any) => () => {
                if (onRowToggleFavourite && rowData) {
                    onRowToggleFavourite(rowData);
                    if (template.component.remoteOperations) {
                        template.component.refresh();
                    }
                }
            };
            col.type = 'string';
            col.width = 60;
            col.alignment = 'center';
            col.headerCellRender = () => <CellFavourite header={true} />;
            col.cellRender = (template: TCellRenderTemplate) =>
                <CellFavourite selected={Boolean(template.value)} callback={callRowToggle(template.data, template)} compact={compact} />;
            col.calculateSortValue = (rowData: TRowData) => rowData[col.field];
            break;

        case 'link':
            col.cellRender = (template: TCellRenderTemplate) =>
                <CellLink value={template.value} onClick={col.onClick} data={template.data} cellElement={template.cellElement} />;
            col.calculateSortValue = (rowData: TRowData) => rowData[col.field];
            break;

        case 'date':
        case 'datetime':
            col.cellRender = (template: TCellRenderTemplate) =>
                <CellDateTime value={template.value} format={col.format} />;
            col.calculateSortValue = (rowData: TRowData) => rowData[col.field];
            break;
    }

    return (
        <Column                                   // map the grid column into devExpress DataGrid column
            key={col.field}
            dataField={col.field}                 // field name in the rowData
            type={col.type as any}
            caption={col.caption}                 // column header text
            format={col.format}

            cssClass={[col.cssClass, col.disabled ? classNameDisabled : null].join(' ')}
            alignment={col.alignment}
            visible={col.visible}
            width={col.width}
            minWidth={col.minWidth}

            isBand={col.isBand}
            ownerBand={col.ownerBandIndex}

            allowSorting={col.allowSorting}
            sortIndex={col.sortIndex}
            sortOrder={col.sortOrder}
            calculateSortValue={col.calculateSortValue}
            sortingMethod={col.sortingMethod}

            headerCellRender={col.headerCellRender}

            //groupIndex={col.groupIndex}
            // known issue in the grid - set this dynamically in onContentReady
            groupCellRender={col.groupCellRender}

            allowFixing={col.fixed}
            //fixed={col.fixed}
            // known issue in the grid - if there are fixed columns and virtual scrolling is enabled then (sometimes) scrollbars can disappear
            // but it works correctly if fixed columns are set after the grid has rendered - so doing this dynamically in the onContentReady callback
            fixedPosition={col.fixed ? 'left' : undefined}
            hidingPriority={col.hidingPriority}

            allowEditing={col.allowEditing}
            cellRender={col.cellRender}
            editCellRender={col.cellRenderEdit}
            calculateCellValue={col.calculateCellValue}
            editorOptions={setEditorOptions(col.onCellValueChange)}
        />
    );
};
